import * as React from 'react'
import Head from 'next/head'

import {
  SeoConfig,
  Post as TesseractPost,
} from '@thg-commerce/enterprise-network'
import {
  formatDistanceToNow,
  getContentRoutePathname,
  i18n,
  PageWrapper,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { spacing, styled } from '@thg-commerce/enterprise-theme'

import { PostContent } from '../../../post-content'
import { FeaturedArticlePost, Post } from '../../types'
import { ArticleMetaRenderer } from '../../renderer/Head/ArticleMetaRenderer'
import { ArticleHeaderRenderer } from '../../renderer/ArticleHeaderRenderer'
import { ArticleProps, Article as ArticleComponent } from '../Article/Article'
import { relatedPostValidator } from '../../validator'

const StyledPageWrapper = styled(PageWrapper)`
  padding-left: ${spacing(2)};
  padding-right: ${spacing(2)};
`

export interface ArticleData {
  post: Post
  content: PostContent
  seoConfig?: SeoConfig | null
}

interface ArticlePageProps extends ArticleData {
  isAmp: boolean
  hasWebP: boolean
}

export const ArticlePage = ({
  isAmp,
  hasWebP,
  post,
  content,
  seoConfig,
}: ArticlePageProps) => {
  const { defaultLocale, originUrl, contentRoutePrefix } = useSiteDefinition()

  const fullArticleUrl = `${originUrl}${getContentRoutePathname(
    contentRoutePrefix,
    post.uri,
  )}`

  const postDate = new Date(post.date)
  const postDateStamp = formatDistanceToNow(postDate, { addSuffix: true })
  const postAuthor = {
    name: post.postAuthorData.name,
    title: post.postAuthorData.subheader,
    profileUri: getContentRoutePathname(
      contentRoutePrefix,
      post.postAuthorData.slug,
    ),
    imageUrl: post?.postAuthorData?.image || undefined,
    imageAlt: i18n('general.viewprofile.text', post.postAuthorData.name),
  }
  const breadcrumbs = {
    enabled: seoConfig?.breadcrumbs?.enabled || false,
    items:
      post?.seo?.customBreadcrumbs?.map((item) => ({
        text: item?.text || '',
        url: item?.path || '',
      })) || [],
  }

  const article: ArticleProps = {
    isAmp,
    breadcrumbs,
    content,
    postTemplate: post.postTemplate,
    tesseractWidgetData: post.tesseractWidgetData,
    signoff: post.signoff ? post.signoff.signoff : undefined,
    authorDetails: {
      isAmp,
      article: {
        date: postDate,
        author: postAuthor,
      },
      additionalAttributes: [postDateStamp],
    },
    authorInformation: {
      isAmp,
      article: {
        date: postDate,
        author: postAuthor,
      },
      bio: post?.postAuthorData.bio,
    },
    socialShareRenderer:
      !isAmp && fullArticleUrl
        ? {
            networks: ['twitter', 'facebook', 'email', 'whatsapp', 'pinterest'],
            title: post.titleDecoded,
            url: fullArticleUrl,
            text: post.seo?.opengraphDescriptionDecoded || '',
          }
        : undefined,
    references: post.citations?.citations
      ? {
          isAmp,
          content: post.citations.citations,
        }
      : undefined,
    relatedArticles: post.postRelatedPostsData?.data
      ? {
          i18nTitle: i18n('article.relatedarticles.title'),
          articles: (post.postRelatedPostsData.data as FeaturedArticlePost[])
            .filter((post) => relatedPostValidator.isValidSync(post))
            .map((post) => {
              const category = post.categories.nodes[0]
              const postDate = new Date(post.date)
              const postDateStamp = formatDistanceToNow(postDate, {
                addSuffix: true,
              })

              return {
                title: post.titleDecoded,
                image: {
                  isAmp,
                  src: [{ url: post.featuredImage?.mediaItemUrl || '' }],
                  alt: post.titleDecoded,
                  height: '100%',
                  width: '100%',
                },
                articleAge: postDateStamp,
                i18nAuthorText: i18n(
                  'content.authorprefix.text',
                  post.postAuthorData.name,
                ),
                category: category.name,
                articleUrl: getContentRoutePathname(
                  contentRoutePrefix,
                  post.uri,
                ),
                categoryUrl: getContentRoutePathname(
                  contentRoutePrefix,
                  category.uri,
                ),
                authorUrl: getContentRoutePathname(
                  contentRoutePrefix,
                  post.author.uri,
                ),
              }
            }),
        }
      : undefined,
  }

  const headI18nText = {
    defaultMetaDescription: i18n('general.page.meta.description.article.text'),
  }

  const hrefLangTags = article.content.head?.hrefLangTags
    ? article.content.head?.hrefLangTags
    : undefined

  const hrefLangTagLinks: JSX.Element[] = []

  if (hrefLangTags) {
    for (const locale of Object.keys(hrefLangTags)) {
      hrefLangTagLinks.push(
        <link rel="alternate" hrefLang={locale} href={hrefLangTags[locale]} />,
      )
    }
  }

  return (
    <React.Fragment>
      <Head>
        <title>{post.seo?.title || post.titleDecoded}</title>
        {ArticleMetaRenderer({
          post,
          defaultLocale,
          articleUrl: fullArticleUrl,
          i18nText: headI18nText,
        })}
        {post.seo?.structuredDataJsonObject && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: post.seo.structuredDataJsonObject,
            }}
          />
        )}
        {hrefLangTagLinks}
      </Head>
      <ArticleHeaderRenderer
        isAmp={isAmp}
        hasWebP={hasWebP}
        post={post as TesseractPost}
      />
      <article>
        <StyledPageWrapper responsiveMargin={true} compactMargin={true}>
          <ArticleComponent {...article} />
        </StyledPageWrapper>
      </article>
    </React.Fragment>
  )
}
